import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Loader2, Search, FileText, Undo2, ChevronLeft, ChevronRight, Tent, RotateCcw, Ban, Plus, Link2, Unlink,
} from 'lucide-react';
import { getReturns, openReturnPdf, voidReturn, getDispatches, linkReturnRedistribution } from '../lib/api';
import RecordReturnDialog from '../components/RecordReturnDialog';

const TYPE_META = {
  return: { label: 'Return', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  reversal: { label: 'Reversal', className: 'bg-red-100 text-red-700 border-red-200' },
};

export default function StockReturns() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const canRecord = ['super_admin', 'admin', 'operator'].includes(user?.role);
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // Exception return + redistribution link: shared order-picker dialog
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState('return'); // 'return' | 'link'
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null); // return record being linked
  const [linking, setLinking] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDispatch, setReturnDispatch] = useState(null);

  const openPicker = (mode, target = null) => {
    setPickerMode(mode);
    setLinkTarget(target);
    setPickerSearch('');
    setPickerResults([]);
    setPickerOpen(true);
  };

  const runPickerSearch = async () => {
    setPickerLoading(true);
    try {
      const res = await getDispatches(pickerSearch.trim() ? { search: pickerSearch.trim() } : {});
      let list = (res.data || []).filter((d) => d.status !== 'reversed');
      if (pickerMode === 'return') {
        list = list.filter(
          (d) => ['ready', 'locked', 'dispatched', 'completed'].includes(d.status) && d.destination_type !== 'event'
        );
      } else if (linkTarget) {
        list = list.filter((d) => d.id !== linkTarget.dispatch_id);
      }
      setPickerResults(list.slice(0, 25));
    } catch {
      toast.error('Search failed');
    } finally {
      setPickerLoading(false);
    }
  };

  const handlePickOrder = async (d) => {
    if (pickerMode === 'return') {
      setPickerOpen(false);
      setReturnDispatch(d);
      setReturnDialogOpen(true);
      return;
    }
    setLinking(true);
    try {
      const res = await linkReturnRedistribution(linkTarget.id, d.id);
      toast.success(res.data.message || 'Redistribution linked');
      setPickerOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to link redistribution');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    setLinking(true);
    try {
      const res = await linkReturnRedistribution(linkTarget.id, null);
      toast.success(res.data.message || 'Redistribution link removed');
      setPickerOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove link');
    } finally {
      setLinking(false);
    }
  };

  const handleVoid = async () => {
    if (!voidTarget) return;
    if (voidReason.trim().length < 5) {
      toast.error('Enter a reason (min 5 characters).');
      return;
    }
    setVoiding(true);
    try {
      const res = await voidReturn(voidTarget.id, { reason: voidReason.trim() });
      toast.success(res.data.message || 'Return voided');
      setVoidTarget(null);
      setVoidReason('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to void return');
    } finally {
      setVoiding(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (search.trim()) params.search = search.trim();
      if (typeFilter !== 'all') params.return_type = typeFilter;
      const res = await getReturns(params);
      setItems(res.data.returns || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load stock returns');
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="stock-returns-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Undo2 className="w-7 h-7 text-amber-600" /> Stock Returns
          </h1>
          <p className="text-muted-foreground mt-1 text-sm max-w-3xl">
            Formal register of all stock returned to the premises — trade-show returns, customer
            returns and full dispatch reversals. Each record has a numbered Return Note PDF for
            the Revenue audit trail.
          </p>
        </div>
        {canRecord && (
          <Button onClick={() => openPicker('return')} data-testid="record-exception-return-btn">
            <Plus className="w-4 h-4 mr-2" /> Record Exception Return
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search return #, dispatch #, customer, event or product…"
                className="pl-9"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                data-testid="returns-search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
              <SelectTrigger className="w-44" data-testid="returns-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="return">Returns only</SelectItem>
                <SelectItem value="reversal">Reversals only</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground ml-auto" data-testid="returns-count">
              {total} record(s)
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-sm text-muted-foreground" data-testid="returns-empty">
              <RotateCcw className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No stock returns recorded yet. Record one from a dispatch row menu or an event page.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Return #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dispatch #</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-center">Lines</TableHead>
                  <TableHead className="text-right">Total Qty</TableHead>
                  <TableHead>Recorded By</TableHead>
                  <TableHead className="text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => {
                  const meta = TYPE_META[r.return_type] || TYPE_META.return;
                  const voided = !!r.voided;
                  return (
                    <TableRow key={r.id} className={voided ? 'opacity-60' : ''} data-testid={`return-row-${r.return_number}`}>
                      <TableCell className={`font-mono font-medium ${voided ? 'line-through' : ''}`}>{r.return_number}</TableCell>
                      <TableCell className="text-sm">{r.return_date}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={meta.className}>{meta.label}</Badge>
                          {voided && (
                            <Badge variant="outline" className="bg-gray-200 text-gray-600 border-gray-300" title={`Voided: ${r.void_reason || ''}`} data-testid={`voided-badge-${r.return_number}`}>
                              VOIDED
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link to={`/dispatch/${r.dispatch_id}`} className="font-mono text-sm text-blue-700 hover:underline">
                          {r.dispatch_number}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.event_id ? (
                          <span className="flex items-center gap-1.5">
                            <Tent className="w-3.5 h-3.5 text-emerald-600" />
                            {r.event_name || 'Event'}
                          </span>
                        ) : (r.customer_name || '—')}
                        {r.redistributed_to && (
                          <div
                            className="flex items-center gap-1 text-xs text-blue-700 mt-0.5"
                            data-testid={`redistributed-badge-${r.return_number}`}
                          >
                            <Link2 className="w-3 h-3 shrink-0" />
                            <span>
                              redistributed on{' '}
                              <Link to={`/dispatch/${r.redistributed_to.dispatch_id}`} className="font-mono hover:underline">
                                #{r.redistributed_to.dispatch_number}
                              </Link>
                              {r.redistributed_to.customer_name ? ` (${r.redistributed_to.customer_name})` : ''}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">{r.lines?.length || 0}</TableCell>
                      <TableCell className={`text-right font-mono text-sm ${voided ? 'line-through' : ''}`}>
                        {(r.total_quantity_returned || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.created_by_email}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openReturnPdf(r.id)}
                            data-testid={`return-pdf-${r.return_number}`}
                          >
                            <FileText className="w-3.5 h-3.5 mr-1.5" /> Return Note
                          </Button>
                          {canRecord && !voided && (
                            <Button
                              variant="ghost" size="sm" className="text-blue-700 hover:text-blue-800"
                              title={r.redistributed_to ? 'Change / remove redistribution link' : 'Link the order that redistributed this stock'}
                              onClick={() => openPicker('link', r)}
                              data-testid={`link-redistribution-${r.return_number}`}
                            >
                              <Link2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isAdmin && !voided && (
                            <Button
                              variant="ghost" size="sm" className="text-red-600 hover:text-red-700"
                              title="Void this return (admin)"
                              onClick={() => { setVoidTarget(r); setVoidReason(''); }}
                              data-testid={`void-return-${r.return_number}`}
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Order picker dialog: exception return / redistribution link */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid="order-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pickerMode === 'return' ? (
                <><Undo2 className="w-5 h-5 text-amber-600" /> Record Exception Return</>
              ) : (
                <><Link2 className="w-5 h-5 text-blue-700" /> Link Redistribution — {linkTarget?.return_number}</>
              )}
            </DialogTitle>
            <DialogDescription>
              {pickerMode === 'return'
                ? 'Customer sent stock back? Find the original order it was dispatched on, then record what came back (batch-aware, with a numbered Return Note PDF). Event stock is returned from the event page.'
                : 'Pick the order that re-dispatched the returned stock to another customer. Informational only — no quantities move; it simply closes the paper trail on the register.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {pickerMode === 'link' && linkTarget?.redistributed_to && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
                <span>
                  Currently linked to{' '}
                  <strong className="font-mono">#{linkTarget.redistributed_to.dispatch_number}</strong>
                  {linkTarget.redistributed_to.customer_name ? ` (${linkTarget.redistributed_to.customer_name})` : ''}
                </span>
                <Button variant="outline" size="sm" onClick={handleUnlink} disabled={linking} data-testid="unlink-redistribution-btn">
                  <Unlink className="w-3.5 h-3.5 mr-1.5" /> Remove link
                </Button>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Search by dispatch #, customer or PO…"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runPickerSearch()}
                data-testid="order-picker-search-input"
              />
              <Button onClick={runPickerSearch} disabled={pickerLoading} data-testid="order-picker-search-btn">
                {pickerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
            <div className="max-h-[45vh] overflow-y-auto border rounded-md">
              {pickerResults.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground" data-testid="order-picker-empty">
                  {pickerLoading ? 'Searching…' : 'Search for the order.'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dispatch #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pickerResults.map((d) => (
                      <TableRow key={d.id} data-testid={`order-picker-result-${d.dispatch_number}`}>
                        <TableCell className="font-mono font-medium">{d.dispatch_number}</TableCell>
                        <TableCell className="text-sm">{d.customer_name}</TableCell>
                        <TableCell className="text-sm">{d.dispatch_date || '—'}</TableCell>
                        <TableCell><Badge variant="outline">{d.status}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => handlePickOrder(d)}
                            disabled={linking}
                            data-testid={`order-picker-select-${d.dispatch_number}`}
                          >
                            {pickerMode === 'return' ? 'Record Return' : 'Link'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Return dialog (shared core component) */}
      <RecordReturnDialog
        dispatch={returnDispatch}
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        onSuccess={() => load()}
      />

      {/* Void return dialog (admin) */}
      <Dialog open={!!voidTarget} onOpenChange={(open) => { if (!open) setVoidTarget(null); }}>
        <DialogContent className="sm:max-w-[460px]" data-testid="void-return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" /> Void Return {voidTarget?.return_number}
            </DialogTitle>
            <DialogDescription>
              For returns recorded in error. The record stays in the register (struck-through,
              PDF watermarked VOID) and the returned quantities are rolled back off the dispatch —
              reconciliations auto-correct. This cannot be un-voided.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="voidReason">Reason *</Label>
            <Textarea
              id="voidReason" rows={2} maxLength={500}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Quantity entered incorrectly — actual return was 6, not 8"
              data-testid="void-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidTarget(null)} disabled={voiding}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleVoid}
              disabled={voiding || voidReason.trim().length < 5}
              data-testid="confirm-void-btn"
            >
              {voiding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              Void Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
